import { setCSS } from '@gimlite/common';
import { observer } from 'mobx-react-lite';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { usePortalZIndex } from '../../hooks/usePortalZIndex';
import { useStoreUIV2 } from '../../hooks/useStore.hook';
import { ThemeStoreContextType } from '../../stores/theme.store';
import './apploader.component.scss';

export const AppLoaderContext = createContext<{
  isLoading: boolean;
  setIsLoading: ((isLoading: boolean) => void) | null;
}>({
  isLoading: false,
  setIsLoading: null,
});

export declare namespace AppLoaderStartType {
  type Props = {
    className?: string;

    defaultTheme: ThemeStoreContextType['theme'];
    item: React.ReactNode | JSX.Element;
    type: 'logo' | 'loader';
    color: {
      dark: string;
      light: string;
    };
    children: React.ReactNode;
  };
}

export const AppLoaderStart = observer(
  ({
    children,
    defaultTheme,
    item,
    type,
    color,
    className = '',
  }: AppLoaderStartType.Props) => {
    const { ThemeStore } = useStoreUIV2();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const zIndex = usePortalZIndex(!isLoading);

    const currentTheme = ThemeStore.getTheme() || defaultTheme;

    useEffect(() => {
      setCSS(
        '--color-primary',
        currentTheme === 'dark' ? color.dark : color.light,
      );
    }, [color, currentTheme]);

    return (
      <AppLoaderContext.Provider value={{ isLoading, setIsLoading }}>
        <div className={`appLoader ${className}`}>
          <div
            className={`appLoader__content appLoader__content--${isLoading ? 'progress' : 'stop'}`}
            style={{ zIndex: zIndex ?? undefined }}
          >
            {type === 'logo' ? (
              <div
                style={{
                  fill: currentTheme === 'light' ? color.light : color.dark,
                  color: currentTheme === 'light' ? color.light : color.dark,
                }}
                className={`appLoader__content__logo`}
              >
                {item}
              </div>
            ) : (
              item
            )}
          </div>

          {children}
        </div>
      </AppLoaderContext.Provider>
    );
  },
);

export declare namespace AppLoaderEndType {
  type Props = {
    children: any;
  };
}

export const AppLoaderEnd = ({ children }: AppLoaderEndType.Props) => {
  const { setIsLoading } = useContext(AppLoaderContext);

  useEffect(() => {
    if (setIsLoading) {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  return children ? children : null;
};
